// ========================================
// FIREBASE CUSTOM CLAIMS SERVICE
// ========================================
// Client-side integration for Firebase Custom Claims system
// Handles token refresh, claims validation, and seamless integration
// with existing auth architecture

import { User, getIdToken } from 'firebase/auth'
import { UserRole, UserRoles } from '../../constants/userRoles'
import { logger } from '../logger'

// ====================
// TYPE DEFINITIONS
// ====================

export interface CustomClaims {
  role: UserRole
  corporateAccountId: string | null
  roleLevel: number
  privileges: string[]
  accountStatus: string
  profileComplete: boolean
  firstName: string
  lastName: string
  timezone: string
  claimsVersion: number
  lastUpdated: string
}

export interface ClaimsRefreshResult {
  success: boolean
  claims?: CustomClaims
  error?: string
  needsReauth?: boolean
}

export interface ClaimsValidationResult {
  valid: boolean
  claims?: CustomClaims
  errors: string[]
  performanceGain?: number
}

// ====================
// CONSTANTS
// ====================

const CLAIMS_REFRESH_THRESHOLD = 5 * 60 * 1000 // 5 minutes
const MAX_RETRY_ATTEMPTS = 3
const RETRY_DELAY_MS = 1000
const CACHE_TTL_MS = 5 * 60 * 1000 // 5 minutes cache TTL

// Role hierarchy for client-side validation
const ROLE_HIERARCHY: Record<UserRole, number> = {
  [UserRoles.SUPER_ADMIN]: 4,
  [UserRoles.CORPORATE]: 3,
  [UserRoles.ADMIN]: 2,
  [UserRoles.MANAGEMENT]: 1,
  [UserRoles.TENANT]: 0,
}

// ====================
// CLAIMS EXTRACTION & VALIDATION
// ====================

/**
 * Extract custom claims from Firebase ID token
 * @param user - Firebase user object
 * @param forceRefresh - Force token refresh
 * @returns Custom claims or null
 */
export async function extractCustomClaims(
  user: User | null,
  forceRefresh: boolean = false
): Promise<CustomClaims | null> {
  if (!user) {
    return null
  }

  try {
    const idTokenResult = await user.getIdTokenResult(forceRefresh)
    const claims = idTokenResult.claims

    // Check if custom claims exist
    if (!claims.role) {
      logger.debug('No custom claims found in token', { uid: user.uid })
      return null
    }

    // Validate and structure claims
    const customClaims: CustomClaims = {
      role: claims.role as UserRole,
      corporateAccountId: (claims.corporateAccountId as string) || null,
      roleLevel:
        (claims.roleLevel as number) ||
        ROLE_HIERARCHY[claims.role as UserRole] ||
        0,
      privileges: (claims.privileges as string[]) || [],
      accountStatus: (claims.accountStatus as string) || 'active',
      profileComplete: (claims.profileComplete as boolean) || false,
      firstName: (claims.firstName as string) || '',
      lastName: (claims.lastName as string) || '',
      timezone: (claims.timezone as string) || 'America/New_York',
      claimsVersion: (claims.claimsVersion as number) || 0,
      lastUpdated: (claims.lastUpdated as string) || new Date().toISOString(),
    }

    // Cache claims for performance
    cacheUserClaims(user.uid, customClaims)

    logger.info('Custom claims extracted successfully', {
      uid: user.uid,
      role: customClaims.role,
      claimsVersion: customClaims.claimsVersion,
    })

    return customClaims
  } catch (error) {
    logger.error('Failed to extract custom claims', {
      uid: user.uid,
      error: error instanceof Error ? error.message : 'Unknown error',
      forceRefresh,
    })
    return null
  }
}

/**
 * Validate custom claims structure and content
 * @param claims - Claims to validate
 * @returns Validation result
 */
export function validateCustomClaims(
  claims: Record<string, unknown>
): ClaimsValidationResult {
  const errors: string[] = []

  // Check required fields
  if (!claims.role) {
    errors.push('Role is required in custom claims')
  } else if (
    !(Object.values(UserRoles) as string[]).includes(claims.role as string)
  ) {
    errors.push(`Invalid role in custom claims: ${claims.role}`)
  }

  if (claims.roleLevel !== undefined && typeof claims.roleLevel !== 'number') {
    errors.push('Role level must be a number')
  }

  if (claims.privileges !== undefined && !Array.isArray(claims.privileges)) {
    errors.push('Privileges must be an array')
  }

  // Validate role hierarchy consistency
  if (claims.role && claims.roleLevel !== undefined) {
    const expectedLevel = ROLE_HIERARCHY[claims.role as UserRole]
    if (expectedLevel !== undefined && claims.roleLevel !== expectedLevel) {
      errors.push(
        `Role level mismatch: expected ${expectedLevel}, got ${claims.roleLevel}`
      )
    }
  }

  const valid = errors.length === 0

  return {
    valid,
    claims: valid ? (claims as unknown as CustomClaims) : undefined,
    errors,
    performanceGain: valid
      ? calculatePerformanceGain(claims as unknown as CustomClaims)
      : undefined,
  }
}

/**
 * Calculate estimated performance gain from using claims
 * @param claims - Custom claims
 * @returns Estimated performance improvement percentage
 */
function calculatePerformanceGain(claims: CustomClaims): number {
  // Base performance gain from eliminating document fetches
  let gain = 80 // 80% base improvement

  // Additional gains based on complexity
  if (claims.corporateAccountId) {
    gain += 5 // Multi-tenancy checks are especially expensive
  }

  if (claims.privileges.length > 0) {
    gain += 3 // Privilege checks add complexity
  }

  if (claims.roleLevel >= ROLE_HIERARCHY[UserRoles.ADMIN]) {
    gain += 2 // Admin+ operations are more complex
  }

  return Math.min(gain, 95) // Cap at 95% improvement
}

// ====================
// CLAIMS REFRESH & MANAGEMENT
// ====================

/**
 * Refresh user claims from server
 * @param user - Firebase user object
 * @param retryCount - Current retry attempt
 * @returns Refresh result
 */
export async function refreshUserClaims(
  user: User | null,
  retryCount: number = 0
): Promise<ClaimsRefreshResult> {
  if (!user) {
    return { success: false, error: 'No user provided' }
  }

  try {
    logger.info('Refreshing user claims', { uid: user.uid, retryCount })

    // Call the Firebase Function to refresh claims
    const idToken = await getIdToken(user)

    const response = await fetch('/refreshClaims', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ userId: user.uid }),
    })

    if (!response.ok) {
      throw new Error(
        `Failed to refresh claims: ${response.status} ${response.statusText}`
      )
    }

    const result = await response.json()

    if (!result.success) {
      throw new Error(result.message || 'Claims refresh failed')
    }

    // Force token refresh to get updated claims
    const claims = await extractCustomClaims(user, true)

    if (!claims) {
      throw new Error('Failed to extract refreshed claims')
    }

    logger.info('User claims refreshed successfully', {
      uid: user.uid,
      role: claims.role,
      claimsVersion: claims.claimsVersion,
    })

    return { success: true, claims }
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error'

    logger.error('Failed to refresh user claims', {
      uid: user.uid,
      error: errorMessage,
      retryCount,
    })

    // Retry logic
    if (retryCount < MAX_RETRY_ATTEMPTS) {
      logger.info('Retrying claims refresh', {
        uid: user.uid,
        retryCount: retryCount + 1,
      })

      await new Promise(resolve =>
        setTimeout(resolve, RETRY_DELAY_MS * (retryCount + 1))
      )
      return refreshUserClaims(user, retryCount + 1)
    }

    // Check if this is an auth error requiring re-authentication
    const needsReauth =
      errorMessage.includes('Unauthorized') ||
      errorMessage.includes('Invalid token') ||
      errorMessage.includes('401')

    return {
      success: false,
      error: errorMessage,
      needsReauth,
    }
  }
}

/**
 * Check if claims need refresh based on age and version
 * @param claims - Current claims
 * @returns Whether refresh is needed
 */
export function shouldRefreshClaims(claims: CustomClaims | null): boolean {
  if (!claims) {
    return true // No claims means we definitely need them
  }

  // Check claims age
  const lastUpdated = new Date(claims.lastUpdated)
  const age = Date.now() - lastUpdated.getTime()

  if (age > CLAIMS_REFRESH_THRESHOLD) {
    logger.debug('Claims refresh needed due to age', {
      age,
      threshold: CLAIMS_REFRESH_THRESHOLD,
    })
    return true
  }

  // Check if claims version is too old (implement versioning strategy)
  const currentTime = Date.now()
  const claimsAge = currentTime - claims.claimsVersion

  if (claimsAge > CLAIMS_REFRESH_THRESHOLD * 2) {
    logger.debug('Claims refresh needed due to version age', { claimsAge })
    return true
  }

  return false
}

/**
 * Auto-refresh claims if needed
 * @param user - Firebase user object
 * @returns Current or refreshed claims
 */
export async function autoRefreshClaims(
  user: User | null
): Promise<CustomClaims | null> {
  if (!user) {
    return null
  }

  try {
    // Get current claims
    const currentClaims = await extractCustomClaims(user, false)

    // No server refresh: the `refreshClaims` function has never been
    // deployed (D-N-081), so the call always 404'd (with retries) and fell
    // back to these same claims (PB-98 4). Callers use the Firestore record
    // when this is null.
    return currentClaims
  } catch (error) {
    logger.error('Auto-refresh claims failed', {
      uid: user.uid,
      error: error instanceof Error ? error.message : 'Unknown error',
    })

    // Return cached claims on error
    return getCachedUserClaims(user.uid)
  }
}

// ====================
// CLAIMS CACHING (MEMORY-ONLY FOR SECURITY)
// ====================

/**
 * In-memory cache for user claims (XSS-safe, no sessionStorage)
 * Security: Memory-only cache prevents XSS attacks from accessing claims
 * via sessionStorage manipulation
 */
const claimsCache = new Map<
  string,
  { claims: CustomClaims; timestamp: number }
>()

/**
 * Cache user claims in memory (XSS-safe)
 * @param uid - User ID
 * @param claims - Claims to cache
 */
function cacheUserClaims(uid: string, claims: CustomClaims): void {
  try {
    claimsCache.set(uid, {
      claims,
      timestamp: Date.now(),
    })

    logger.debug('Cached user claims in memory', { uid, role: claims.role })
  } catch (error) {
    logger.warn('Failed to cache user claims', { uid, error })
  }
}

/**
 * Get cached user claims from memory
 * @param uid - User ID
 * @returns Cached claims or null
 */
function getCachedUserClaims(uid: string): CustomClaims | null {
  try {
    const cached = claimsCache.get(uid)
    if (!cached) {
      return null
    }

    // Check cache age
    const age = Date.now() - cached.timestamp
    if (age > CACHE_TTL_MS) {
      claimsCache.delete(uid)
      logger.debug('Cache expired, removed stale claims', { uid, age })
      return null
    }

    return cached.claims
  } catch (error) {
    logger.warn('Failed to get cached user claims', { uid, error })
    claimsCache.delete(uid)
    return null
  }
}

/**
 * Clear cached user claims from memory
 * @param uid - User ID (optional, clears all if not provided)
 */
export function clearCachedClaims(uid?: string): void {
  try {
    if (!uid) {
      claimsCache.clear()
      logger.debug('Cleared all cached claims')
      return
    }

    claimsCache.delete(uid)
    logger.debug('Cleared cached claims for user', { uid })
  } catch (error) {
    logger.warn('Failed to clear cached claims', { uid, error })
  }
}

// ====================
// PERMISSION HELPERS
// ====================

/**
 * Check if user has specific role using claims
 * @param claims - User claims
 * @param requiredRole - Required role
 * @returns Whether user has required role or higher
 */
export function hasRoleFromClaims(
  claims: CustomClaims | null,
  requiredRole: UserRole
): boolean {
  if (!claims) {
    return false
  }

  const userLevel = claims.roleLevel
  const requiredLevel = ROLE_HIERARCHY[requiredRole]

  return userLevel >= requiredLevel
}

/**
 * Check if user has specific privilege using claims
 * @param claims - User claims
 * @param privilege - Required privilege
 * @returns Whether user has privilege
 */
export function hasPrivilegeFromClaims(
  claims: CustomClaims | null,
  privilege: string
): boolean {
  if (!claims) {
    return false
  }

  return claims.privileges.includes(privilege)
}

/**
 * Check if user belongs to same corporate account using claims
 * @param claims - User claims
 * @param targetCorporateAccountId - Target corporate account ID
 * @returns Whether user belongs to same corporate account
 */
export function isSameCorporateAccountFromClaims(
  claims: CustomClaims | null,
  targetCorporateAccountId: string | null
): boolean {
  if (!claims || !targetCorporateAccountId) {
    return false
  }

  return claims.corporateAccountId === targetCorporateAccountId
}

// ====================
// PERFORMANCE MONITORING
// ====================

/**
 * Track claims performance improvements
 * @param operation - Operation type
 * @param performanceGain - Performance improvement percentage
 */
export function trackClaimsPerformance(
  operation: string,
  performanceGain: number
): void {
  try {
    // Log performance metrics
    logger.info('Claims performance improvement', {
      operation,
      performanceGain,
      timestamp: new Date().toISOString(),
    })

    // Track in analytics if available
    const win = window as unknown as Record<
      string,
      ((...args: unknown[]) => void) | undefined
    >
    if (typeof window !== 'undefined' && typeof win.gtag === 'function') {
      win.gtag('event', 'claims_performance', {
        event_category: 'performance',
        event_label: operation,
        value: Math.round(performanceGain),
      })
    }
  } catch (error) {
    // Don't fail the operation if tracking fails
    logger.debug('Performance tracking failed', { operation, error })
  }
}

/**
 * Get performance metrics summary
 * @returns Performance summary
 */
export function getPerformanceMetrics(): {
  claimsEnabled: boolean
  averageGain: number
  operationsCount: number
} {
  // This would typically integrate with your analytics system
  // For now, return estimated metrics
  return {
    claimsEnabled: true,
    averageGain: 85, // Estimated 85% performance improvement
    operationsCount: 0, // Would track actual operations
  }
}

// ====================
// EXPORTS
// ====================
// Note: All functions are already exported at their declarations
